import React, { useEffect, useRef, useState } from "react";

/**
 * Fades + slides child content into view once it enters the viewport.
 * Uses IntersectionObserver — respects prefers-reduced-motion by defaulting
 * to instantly-visible when the user has motion disabled.
 *
 * Usage:
 *   <RevealOnScroll delay={100}><h2>My heading</h2></RevealOnScroll>
 */
export default function RevealOnScroll({
  children,
  delay = 0,
  as: Tag = "div",
  className = "",
  once = true,
  y = 20,
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      setVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -80px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [once]);

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : `translateY(${y}px)`,
        transition: `opacity 0.7s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </Tag>
  );
}
